import type { ReactNode } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
    GET_ALL_PROJECTS,
    GET_PROJECT_TAGS_ME,
    GET_PROJECTS_TASKS_SUMMARY,
    GET_PROJECT_SEARCH_FILTERS,
} from '@api/queries/projects';
import type { Project } from '@gql/graphql';
import { OrderDirection } from '@gql/graphql';
import ErrorMessage from '@components/ErrorMessage';
import { useInfoCard } from '@components/InfoCard';
import { useDebounce } from '@hooks/useDebounce';
import { DisplayMode } from '@models/display-mode';
import type { Maybe } from '@models/Maybe';
import { flatMapEdges } from '@utils/flatMapEdges';
import type { ProjectContextValues } from '../models/projectsContext';
import { buildProjectTaskSummaryMap } from '../utils/project-task-summary';
import { mapQueryResultToOptions } from '../utils/mapQueryResultToStringOption';
import {
    buildProjectsWhere,
    DEFAULT_PROJECT_FILTERS,
    hasActiveProjectFilters,
    sortStringOptions,
    type ProjectFiltersState,
} from '../utils/projectSearch';

const ProjectsContext = createContext<ProjectContextValues>({
    projects: { loading: false, data: [] },
    projectCompleteness: {
        byProjectId: {},
        loading: false,
        error: undefined,
    },
    selection: { get: undefined, set: () => null },
    displayMode: { get: DisplayMode.Cards, set: () => null },
    filters: {
        state: { get: DEFAULT_PROJECT_FILTERS, set: () => null },
        hasActive: false,
        clear: () => null,
        loading: false,
        statusOptions: [],
        typeOptions: [],
        tagOptions: [],
    },
    expandInfoCard: () => null,
});

interface ProjectsProviderProps {
    children: ReactNode;
}

export function ProjectsProvider({
    children,
}: ProjectsProviderProps): ReactNode {
    const { setOpen } = useInfoCard();
    const [filtersState, setFiltersState] = useState<ProjectFiltersState>(
        DEFAULT_PROJECT_FILTERS,
    );
    const [selected, setSelected] = useState<Project | undefined>(undefined);
    const [displayModeState, setDisplayModeState] = useState<DisplayMode>(
        DisplayMode.Cards,
    );

    const debouncedSearch = useDebounce(filtersState.search, 250);

    const queryFilters = useMemo(
        () => ({ ...filtersState, search: debouncedSearch }),
        [debouncedSearch, filtersState],
    );
    const where = useMemo(
        () => buildProjectsWhere(queryFilters),
        [queryFilters],
    );

    const { loading, error, data } = useQuery(GET_ALL_PROJECTS, {
        variables: { direction: OrderDirection.Asc, where },
        fetchPolicy: 'cache-and-network',
    });
    const {
        loading: filtersLoading,
        error: filtersError,
        data: filterOptionsData,
    } = useQuery(GET_PROJECT_SEARCH_FILTERS, {
        fetchPolicy: 'cache-first',
    });
    const {
        loading: tagOptionsLoading,
        error: tagOptionsError,
        data: tagOptionsData,
    } = useQuery(GET_PROJECT_TAGS_ME, {
        fetchPolicy: 'cache-and-network',
    });

    const projects = useMemo(
        () => (flatMapEdges(data?.projects) as unknown as Project[]) ?? [],
        [data],
    );
    const projectIds = useMemo(
        () => projects.map((project) => project.id),
        [projects],
    );
    const {
        data: taskSummaryData,
        loading: taskSummaryLoading,
        error: taskSummaryError,
    } = useQuery(GET_PROJECTS_TASKS_SUMMARY, {
        variables: { projectIDs: projectIds },
        skip: projectIds.length === 0,
        fetchPolicy: 'cache-and-network',
    });
    const projectCompleteness = useMemo(
        () => ({
            byProjectId: buildProjectTaskSummaryMap(
                projectIds,
                flatMapEdges(taskSummaryData?.tasks),
            ),
            loading: projectIds.length > 0 && taskSummaryLoading,
            error: taskSummaryError,
        }),
        [projectIds, taskSummaryData, taskSummaryError, taskSummaryLoading],
    );
    const statusOptions = useMemo(
        () =>
            sortStringOptions(
                mapQueryResultToOptions(filterOptionsData?.projectStatuses),
            ),
        [filterOptionsData?.projectStatuses],
    );
    const typeOptions = useMemo(
        () =>
            sortStringOptions(
                mapQueryResultToOptions(filterOptionsData?.projectTypes),
            ),
        [filterOptionsData?.projectTypes],
    );
    const tagOptions = useMemo(
        () =>
            sortStringOptions(
                tagOptionsData?.projectTagsMe.map((tagName) => ({
                    value: tagName,
                    label: tagName,
                })) ?? [],
            ),
        [tagOptionsData?.projectTagsMe],
    );
    const hasActiveFilters = useMemo(
        () => hasActiveProjectFilters(filtersState),
        [filtersState],
    );

    useEffect(() => {
        if (!selected) {
            return;
        }

        const freshSelected = projects.find(
            (project) => project.id === selected.id,
        );

        if (freshSelected !== selected) {
            setSelected(freshSelected);
        }
    }, [projects, selected]);

    const projectsError = error ?? filtersError ?? tagOptionsError;

    if (projectsError) {
        return <ErrorMessage error={projectsError} />;
    }

    return (
        <ProjectsContext.Provider
            value={{
                projects: { loading, data: projects },
                projectCompleteness,
                selection: {
                    get: selected,
                    set: (value: Maybe<Project>) => {
                        setSelected(value ?? undefined);
                    },
                },
                displayMode: {
                    get: displayModeState,
                    set: setDisplayModeState,
                },
                filters: {
                    state: { get: filtersState, set: setFiltersState },
                    hasActive: hasActiveFilters,
                    clear: () => {
                        setFiltersState(DEFAULT_PROJECT_FILTERS);
                    },
                    loading: filtersLoading || tagOptionsLoading,
                    statusOptions,
                    typeOptions,
                    tagOptions,
                },
                expandInfoCard: () => {
                    setOpen(true);
                },
            }}
        >
            {children}
        </ProjectsContext.Provider>
    );
}

export default ProjectsContext;
